'use client';

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useTutorialOptional } from '@/features/tutorial/TutorialContext';
import { hqPlayBoardLand, hqPlayBoardWhoosh } from '../audio/hq-sounds';
import {
  BoardStageProvider,
  type BoardStageAnimationApi,
  type BoardStageContextValue,
  type BoardStagePhase,
} from './board-stage-context';
import {
  BOARD_CROSSFADE_MS,
  BOARD_LAND_AUDIO_DELAY_MS,
  BOARD_PRELOAD_TIMEOUT_MS,
} from './board-stage-timings';
import { BoardStageSlot, type BoardStageSlotProps } from './BoardStageSlot';

/** Strict subset of `BoardStageSlotProps` the stage forwards into
 *  every slot it mounts. The stage controls `boardId`, `phase`,
 *  `isActiveTarget`, and `onReady` itself; everything else is
 *  pass-through from `LobbyClient`. */
export type LobbyBoardStageRenderProps = Omit<
  BoardStageSlotProps,
  'boardId' | 'phase' | 'isActiveTarget' | 'onReady' | 'bypassReadinessGate'
>;

export interface LobbyBoardStageProps extends LobbyBoardStageRenderProps {
  /** The board id from the URL (= `activeBoardId` in `LobbyClient`).
   *  When this changes, the stage mounts a new slot offscreen, waits
   *  for it to signal readiness, then crossfades. */
  targetBoardId: string;
  /** Tree the stage exposes its context to. The actual canvas mount
   *  point is `<LobbyBoardStageCanvas />`, which a descendant places
   *  into the canvas region of `LobbyClient`. Drawers / dev panels
   *  / chip-rail reset all consume `useBoardStageContext()` from this
   *  same children subtree. */
  children: ReactNode;
}

/**
 * Tracks the "we have a slot mounted for this id" lifecycle. The
 * `key` is what React keys the slot subtree on — bumping it forces
 * a fresh mount with empty state, even when the same `boardId` is
 * re-targeted (e.g. user navigates A → B → A — A's second visit
 * should not inherit A's previous `tileAnimStates`).
 */
interface SlotState {
  /** The board this slot is rendering. */
  boardId: string;
  /** Mount-stable key. Two slots with the same boardId can coexist
   *  briefly during a rapid retarget; bumping the key on each new
   *  occurrence keeps React from accidentally reusing one. */
  key: string;
  /** Visible lifecycle phase the slot is in. */
  phase: BoardStagePhase;
}

/**
 * Internal wiring between the state machine in `<LobbyBoardStage>`
 * and the `<LobbyBoardStageCanvas>` child that renders the actual
 * slot pair. Kept private to this module — outside callers go
 * through `useBoardStageContext()` for the public surface.
 */
interface BoardStageInternalValue {
  slots: { current: SlotState; incoming: SlotState | null };
  handleIncomingReady: () => void;
  isTutorialMode: boolean;
  renderProps: LobbyBoardStageRenderProps;
}

const BoardStageInternalContext =
  createContext<BoardStageInternalValue | null>(null);

/**
 * Two-slot crossfade orchestrator for the lobby canvas.
 *
 * ## Lifecycle
 *
 * 1. **Cold mount.** The stage mounts a single slot with
 *    `phase='current'`. There's no outgoing slot, so no crossfade
 *    audio plays. The slot signals `onReady` once its data + first
 *    paint settle; the stage does nothing with that signal because
 *    the cold-mount slot is already at opacity 1.
 * 2. **Target change.** When `targetBoardId` changes, the stage
 *    mounts a SECOND slot with `phase='incoming-loading'` (opacity
 *    0). The first slot keeps painting at `phase='current'`. We
 *    arm a hard ceiling timer at `BOARD_PRELOAD_TIMEOUT_MS` so a
 *    stuck indexer / network hiccup can't wedge the user.
 * 3. **Readiness.** When the incoming slot's `onReady` fires (data
 *    loaded + first frame painted) the stage:
 *      a. plays `hqPlayBoardWhoosh()`,
 *      b. flips the incoming slot to `phase='incoming-fading-in'`,
 *      c. flips the outgoing slot to `phase='outgoing-fading-out'`,
 *      d. schedules `hqPlayBoardLand()` for
 *         `BOARD_LAND_AUDIO_DELAY_MS` later (= the moment slot B
 *         reaches opacity 1),
 *      e. schedules the outgoing-slot unmount for
 *         `BOARD_CROSSFADE_MS` later.
 * 4. **Settled.** The outgoing slot unmounts, the incoming slot's
 *    phase flips to `'current'`, and the stage is idle.
 *
 * ## Rapid retarget mid-fade
 *
 * If `targetBoardId` changes while a transition is in flight:
 *   - The current incoming slot is replaced (new key) with one for
 *     the new id — its readiness gate restarts;
 *   - The outgoing slot keeps painting (the user still sees the
 *     previously-current board);
 *   - The land audio + unmount timers reset.
 *
 * ## Tutorial / mobile / reduced-motion
 *
 * - Tutorial mode bypasses the readiness gate so any tutorial-driven
 *   id change is a hard cut. The script doesn't fetch live data and
 *   would deadlock waiting on `gridLoading=false`.
 * - Mobile and reduced-motion preferences keep the same crossfade
 *   semantics; the audio cues short-circuit on those paths via
 *   gates inside `hqPlayBoardWhoosh` / `hqPlayBoardLand`, and the
 *   CSS `prefers-reduced-motion` block in `globals.css` collapses
 *   the visible fade to a hard cut.
 */
export function LobbyBoardStage({
  targetBoardId,
  children,
  ...renderProps
}: LobbyBoardStageProps) {
  const tutorial = useTutorialOptional();
  const isTutorialMode = !!tutorial;

  // Monotonic counter for slot keys. Each new slot pushed onto the
  // stage gets `slotKeyCounter++`, so a board id revisited later
  // can never collide with a previous slot's React subtree.
  const slotKeyCounterRef = useRef(0);
  const newSlotKey = useCallback((boardId: string) => {
    const next = ++slotKeyCounterRef.current;
    return `${boardId}:${next}`;
  }, []);

  const [slots, setSlots] = useState<{
    current: SlotState;
    incoming: SlotState | null;
  }>(() => ({
    current: {
      boardId: targetBoardId,
      key: `${targetBoardId}:0`,
      phase: 'current',
    },
    incoming: null,
  }));

  // Latest committed `displayedBoardId` exposed via context. Always
  // tracks the slot in `phase='current'` (or the outgoing one mid-
  // fade — same value, just a snapshot).
  const displayedBoardId = slots.current.boardId;
  const isTransitioning = slots.incoming !== null;

  // Active slot animation API — installed by whichever slot has
  // `isActiveTarget=true`. The ref is mutated synchronously inside
  // the `__registerActiveSlot` cleanup so a slot that just lost
  // active status can't keep handling drawer dispatches.
  const activeApiRef = useRef<BoardStageAnimationApi | null>(null);

  // ─── Stage context surface ────────────────────────────────────────
  const stageValue = useMemo<BoardStageContextValue>(
    () => ({
      displayedBoardId,
      interactionsLocked: isTransitioning,
      playBuyoutAnimation: (seatIndex, prev, next) => {
        activeApiRef.current?.playBuyoutAnimation(seatIndex, prev, next);
      },
      playClaimAnimation: (seatIndex, next) => {
        activeApiRef.current?.playClaimAnimation(seatIndex, next);
      },
      triggerTileAnim: (kind, count) => {
        activeApiRef.current?.triggerTileAnim(kind, count);
      },
      __registerActiveSlot: (api) => {
        activeApiRef.current = api;
        return () => {
          if (activeApiRef.current === api) {
            activeApiRef.current = null;
          }
        };
      },
    }),
    [displayedBoardId, isTransitioning],
  );

  // ─── Tutorial bridge wiring ───────────────────────────────────────
  // The tutorial overlay registers a single bridge for the lifetime
  // of a tutorial run; we install a permanent forwarder here so the
  // bridge stays valid across slot swaps. Calls land on whichever
  // slot is currently active. Uses `useEffect` rather than the slot's
  // own `isActiveTarget` flow so we don't churn the tutorial's ref
  // on every navigation.
  useEffect(() => {
    if (!tutorial) return;
    return tutorial.registerTileAnimBridge((seatIndex, state) => {
      activeApiRef.current?.setSingleTileAnim(seatIndex, state);
    });
  }, [tutorial]);

  // ─── Crossfade orchestration ──────────────────────────────────────
  /**
   * Begin (or update) a transition toward `nextBoardId`.
   *
   * Called from a `useEffect` that observes `targetBoardId`. When
   * the stage is already idle the new slot is mounted offscreen
   * and the readiness handshake takes over. When a transition is
   * already in flight, we replace the incoming slot in-place so
   * the user always crossfades into the *latest* requested board
   * even if they spam-clicked through multiple cards.
   */
  const startTransitionTo = useCallback(
    (nextBoardId: string) => {
      setSlots((prev) => {
        if (
          prev.current.boardId === nextBoardId &&
          prev.incoming === null
        ) {
          return prev;
        }
        // Same board re-targeted while a fade was already
        // landing it — no-op, the in-flight fade will land on the
        // right id. (A new mount would discard its readiness gate
        // for nothing.)
        if (prev.incoming?.boardId === nextBoardId) {
          return prev;
        }
        // Reverting back to the current board mid-fade: cancel
        // the incoming slot so the user stays on the original
        // board without a redundant out-and-back fade. Covers the
        // A → B (mid-fade) → A user gesture.
        if (
          prev.incoming !== null &&
          prev.current.boardId === nextBoardId
        ) {
          return {
            current: { ...prev.current, phase: 'current' },
            incoming: null,
          };
        }
        // Replace the incoming slot (or create one if the stage
        // was idle). Bump the key so the slot subtree is fresh.
        return {
          current: prev.current,
          incoming: {
            boardId: nextBoardId,
            key: newSlotKey(nextBoardId),
            phase: 'incoming-loading',
          },
        };
      });
    },
    [newSlotKey],
  );

  // Drive transitions off the URL-active board id.
  useEffect(() => {
    startTransitionTo(targetBoardId);
  }, [targetBoardId, startTransitionTo]);

  // ─── Readiness gate / crossfade trigger ──────────────────────────
  /**
   * Schedule the post-readiness side effects: audio + outgoing
   * slot unmount + final phase commit. Centralised here so the
   * timeout-based hard cut and the real readiness signal both
   * land through the same path.
   */
  const startCrossfadePhase = useCallback(() => {
    setSlots((prev) => {
      if (!prev.incoming) return prev;
      if (prev.incoming.phase !== 'incoming-loading') return prev;
      return {
        current: { ...prev.current, phase: 'outgoing-fading-out' },
        incoming: { ...prev.incoming, phase: 'incoming-fading-in' },
      };
    });
  }, []);

  // Capture the latest startCrossfadePhase in a ref so the slot's
  // `onReady` prop has a stable identity. The slot's readiness
  // useEffect deps include `onReady`; if the prop identity churns,
  // the gate could re-fire and dispatch the crossfade twice. With
  // a ref the prop stays stable for the slot's lifetime.
  const startCrossfadePhaseRef = useRef(startCrossfadePhase);
  startCrossfadePhaseRef.current = startCrossfadePhase;
  const handleIncomingReady = useCallback(() => {
    startCrossfadePhaseRef.current();
  }, []);

  // Schedule the audio + unmount when we enter the visible-fade
  // phase. This effect runs once per fade because both slot phases
  // flip in lockstep and `incomingPhase` only changes on enter /
  // exit of the visible-fade window.
  const incomingKey = slots.incoming?.key ?? null;
  const incomingPhase = slots.incoming?.phase ?? null;
  useEffect(() => {
    if (incomingPhase !== 'incoming-fading-in') return;
    // Whoosh fires the moment slot A's opacity begins decreasing.
    // The audio gate inside `hqPlayBoardWhoosh` already short-
    // circuits in muted contexts (mobile, tutorial via gates
    // upstream), so we can call it unconditionally here.
    hqPlayBoardWhoosh();
    const landTimer = window.setTimeout(() => {
      hqPlayBoardLand();
    }, BOARD_LAND_AUDIO_DELAY_MS);
    const settleTimer = window.setTimeout(() => {
      setSlots((prev) => {
        if (!prev.incoming) return prev;
        if (prev.incoming.phase !== 'incoming-fading-in') return prev;
        return {
          current: { ...prev.incoming, phase: 'current' },
          incoming: null,
        };
      });
    }, BOARD_CROSSFADE_MS);
    return () => {
      window.clearTimeout(landTimer);
      window.clearTimeout(settleTimer);
    };
  }, [incomingPhase, incomingKey]);

  // ─── Hard-cut fallback (preload timeout / tutorial) ──────────────
  // If readiness hasn't fired by `BOARD_PRELOAD_TIMEOUT_MS`, force
  // the crossfade. Better a hard cut than wedging the user on the
  // outgoing board. Tutorial mode collapses to a synchronous cut
  // because the script doesn't fetch live data.
  useEffect(() => {
    if (!incomingKey) return;
    if (incomingPhase !== 'incoming-loading') return;
    if (isTutorialMode) {
      const t = window.setTimeout(() => startCrossfadePhase(), 0);
      return () => window.clearTimeout(t);
    }
    const timeout = window.setTimeout(() => {
      startCrossfadePhase();
    }, BOARD_PRELOAD_TIMEOUT_MS);
    return () => window.clearTimeout(timeout);
  }, [incomingKey, incomingPhase, isTutorialMode, startCrossfadePhase]);

  // ─── Internal context for `<LobbyBoardStageCanvas>` ──────────────
  // The internal context exists so LobbyClient's render can place
  // the slots into the canvas region while the public stage context
  // wraps the full surrounding tree (drawers, dev panels, chip rail
  // reset). `renderProps` change every commit because the parent
  // recomputes them; that's the desired behaviour — the slot pair
  // re-renders with the latest tile-click handler / sticky tooltip /
  // etc.
  const internalValue = useMemo<BoardStageInternalValue>(
    () => ({
      slots,
      handleIncomingReady,
      isTutorialMode,
      renderProps,
    }),
    [slots, handleIncomingReady, isTutorialMode, renderProps],
  );

  return (
    <BoardStageProvider value={stageValue}>
      <BoardStageInternalContext.Provider value={internalValue}>
        {children}
      </BoardStageInternalContext.Provider>
    </BoardStageProvider>
  );
}

/**
 * Renders the actual two-slot crossfade DOM. Mount this as a
 * descendant of `<LobbyBoardStage>` at the spot in the layout
 * where the canvas should appear. The component is intentionally
 * unstyled at the wrapper level — its inner `absolute inset-0`
 * div fills whatever positioned ancestor it ends up inside, just
 * like the original canvas mount block did.
 */
export function LobbyBoardStageCanvas() {
  const internal = useContext(BoardStageInternalContext);
  if (!internal) {
    throw new Error(
      '<LobbyBoardStageCanvas /> must be rendered inside <LobbyBoardStage>',
    );
  }
  const { slots, handleIncomingReady, isTutorialMode, renderProps } = internal;

  // Active-target rules:
  //   • Idle (one slot)      → that slot is the active target.
  //   • Transitioning         → the incoming slot is the active
  //                             target. The user has navigated to
  //                             its boardId; the outgoing slot is
  //                             on borrowed time, so drawer-driven
  //                             animations should land on the new
  //                             one.
  const currentIsActive = !slots.incoming;
  const incomingIsActive = !!slots.incoming;

  return (
    <div className="absolute inset-0">
      <BoardStageSlot
        key={slots.current.key}
        boardId={slots.current.boardId}
        phase={slots.current.phase}
        isActiveTarget={currentIsActive}
        bypassReadinessGate={isTutorialMode}
        {...renderProps}
      />
      {slots.incoming ? (
        <BoardStageSlot
          key={slots.incoming.key}
          boardId={slots.incoming.boardId}
          phase={slots.incoming.phase}
          isActiveTarget={incomingIsActive}
          onReady={handleIncomingReady}
          bypassReadinessGate={isTutorialMode}
          {...renderProps}
        />
      ) : null}
    </div>
  );
}
